import store from 'app-components/utils/redux-store';
//
import { DateTime } from 'luxon';
import memoizeOne from 'memoize-one';
import numeral from 'numeral';

import { accountsSelectors, accountsTypes } from 'app-components/flux/accounts';
import { chartOfAccountsTypes, chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import {
  customFrequencyUtils,
  scheduledTransactionsSelectors,
  scheduledTransactionsTypes,
} from 'app-components/flux/scheduled-transactions';
import { FrequencyEnum } from 'app-components/flux/scheduled-transactions/frequency';
import { transactionsUtils } from 'app-components/flux/transactions';

export const getCategoryString = (schTxn, categoriesById, accountsById) => {
  const coa = schTxn && schTxn.coa;
  if (!coa) {
    return 'Not Found';
  }

  if (transactionsUtils.isSplitTxn(schTxn)) {
    return '--- Split ---';
  }

  const category = {
    [chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED]: () => 'Uncategorized',
    [chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT]: () =>
      chartOfAccountsUtils.getBalanceAdjustmentString(coa.id),
    [chartOfAccountsTypes.CoaTypeEnum.ACCOUNT]: () => {
      const acct = accountsById.get(coa.id);
      return acct && acct.name;
    },
    default: () => {
      const cat = categoriesById.get(coa.id);

      return cat && cat.name;
    },
  };

  return (category[coa.type] || category.default)() || 'Not Found';
};

// This getter will return a string with the next payment instance and date of the scheduled transaction
const getNextDateFormatted = (firstUpcomingAmount, dueOn, privacyModeMask) => {
  const formattedAmount = privacyModeMask(numeral(firstUpcomingAmount).format('$0,0.00'));
  if (dueOn && firstUpcomingAmount) {
    // If date and amount exist
    return `Next: ${formattedAmount} on ${DateTime.fromISO(dueOn).toLocaleString(DateTime.DATE_MED)}`;
  }
  if (dueOn) return `Next: ${DateTime.fromISO(dueOn).toLocaleString(DateTime.DATE_MED)}`; // If only date exists
  if (firstUpcomingAmount) return `Next: ${formattedAmount}`; // If only amount exists
  return 'Next: Unknown'; // error case
};
export const userFriendlyTxnNames = memoizeOne(
  (scheduledtxn, categoriesById, accountsById, firstUpcomingTxn, privacyModeMask) => {
    const accountInfo =
      scheduledtxn &&
      scheduledtxn.transaction &&
      scheduledtxn.transaction.accountId &&
      accountsSelectors.getAccountById(undefined, scheduledtxn.transaction.accountId);
    const accountTypeNode =
      scheduledtxn &&
      scheduledtxn.transaction &&
      scheduledtxn.transaction.accountId &&
      accountInfo &&
      accountsTypes.getAccountTypeNodeByQcsType(accountInfo.type, accountInfo.subType);
    const scheduleTxnType = scheduledtxn.type;
    const dueOn = (firstUpcomingTxn && firstUpcomingTxn.postedOn) || scheduledtxn.dueOn;
    // used to show firstUpcomingTransaction.amount in Simplifi if it existed. Now show same across WF/Simplifi
    const amount = scheduledtxn.transaction && scheduledtxn.transaction.amount;
    const firstUpcomingAmount = firstUpcomingTxn ? Math.abs(firstUpcomingTxn.amount) : '';
    return {
      id: scheduledtxn && scheduledtxn.id,
      clientId: scheduledtxn.transaction && scheduledtxn.transaction.clientId,
      accountId: scheduledtxn.transaction && scheduledtxn.transaction.accountId,
      payeeName: (scheduledtxn.transaction && scheduledtxn.transaction.payee) || 'New Series',
      amount: amount || 0,
      accountName: (accountInfo && accountInfo.name) || '[Account Not Synced]',
      frequency: customFrequencyUtils.recurrenceRowAlias(scheduledtxn.recurrence) || 'Custom',
      nextDate: dueOn && DateTime.fromISO(dueOn).toLocaleString(),
      nextDateFormatted: getNextDateFormatted(firstUpcomingAmount, dueOn, privacyModeMask),
      recurrence: scheduledtxn.recurrence,
      endDate:
        scheduledtxn.recurrence &&
        scheduledtxn.recurrence.endOn &&
        DateTime.fromISO(scheduledtxn.recurrence.endOn).toLocaleString(),
      endDateFormatted:
        scheduledtxn.recurrence &&
        scheduledtxn.recurrence.endOn &&
        DateTime.fromISO(scheduledtxn.recurrence.endOn).toLocaleString(DateTime.DATE_MED),
      subtype:
        scheduleTxnType === scheduledTransactionsTypes.STTypeEnum.TRANSFER
          ? 'Transfer from'
          : accountTypeNode && accountTypeNode.displayName,
      type: scheduleTxnType === scheduledTransactionsTypes.STTypeEnum.TRANSFER ? 'Transfer to' : scheduleTxnType,
      category: getCategoryString(scheduledtxn.transaction, categoriesById, accountsById),
    };
  },
);

// THIS IS NOT COMPLETE, will need to add additional cases for various recurrence types
export const calcMontlyAverageForScheduledTxn = (schedTxn) => {
  let { amount } = schedTxn.transaction;
  switch (schedTxn.recurrence.frequency) {
    case FrequencyEnum.WEEKLY:
      amount *= 4;
      break;
    case FrequencyEnum.YEARLY:
      amount = amount ? amount / 12 : amount;
      break;
    case FrequencyEnum.DAILY:
      amount *= 30;
      break;
    case FrequencyEnum.MONTHLY:
      if (schedTxn.recurrence.byMonthDay && schedTxn.recurrence.byMonthDay.length > 1) {
        amount *= schedTxn.recurrence.byMonthDay.length;
      }
      break;

    default:
      break;
  }
  return amount / (schedTxn.recurrence.interval || 1);
};

export const retrieveModel = (stModelId) => {
  const state = store.getState();
  return scheduledTransactionsSelectors.getScheduledTransactionById(state, stModelId);
};
